import type { AlgoliaSearchOptions, SearchClient } from 'algoliasearch';
import algoliasearch from 'algoliasearch';
import invariant from 'tiny-invariant';

import { getLocale } from '@libs/core';

const getAlgoliaAppId = (): string => process.env.ALGOLIA_APP_ID?.trim() ?? '';

const emptyPublicAlgoliaCredentials = (): boolean =>
    Boolean(process.env.NEXT_PUBLIC_EPHEMERAL?.trim()) || process.env.NODE_ENV === 'production';

const requirePublic = (name: 'NEXT_PUBLIC_ALGOLIA_APP_ID' | 'NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY', value: string) => {
    if (value) return value;
    if (process.env.NODE_ENV === 'development') {
        throw new Error(
            `[Algolia] Missing ${name}. Run \`pnpm run env\` and select dev env for this app to run locally.`
        );
    }
    throw new Error(`[Algolia] ${name} is not set (NODE_ENV=${process.env.NODE_ENV ?? 'unset'}).`);
};

const publicAppId = (): string => {
    if (emptyPublicAlgoliaCredentials()) return '';
    return requirePublic('NEXT_PUBLIC_ALGOLIA_APP_ID', process.env.NEXT_PUBLIC_ALGOLIA_APP_ID?.trim() ?? '');
};

const publicSearchKey = (): string => {
    if (emptyPublicAlgoliaCredentials()) return '';
    return requirePublic(
        'NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY',
        process.env.NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY?.trim() ?? ''
    );
};

interface SearchClientOptions {
    admin?: boolean;
    caller?: string;
}

const buildSearchOptions = (
    locale: ReturnType<typeof getLocale>,
    caller?: string
): AlgoliaSearchOptions | undefined => {
    const headers = caller ? { headers: { 'x-petm-algolia-caller': caller } } : {};
    const hosts: Record<ReturnType<typeof getLocale>, string | undefined> = {
        'en-US': process.env.NEXT_PUBLIC_ALGOLIA_HOST_URL,
        'en-CA': process.env.NEXT_PUBLIC_ALGOLIA_HOST_URL_CA,
    };
    const host = hosts[locale];

    if (globalThis.window === undefined && !emptyPublicAlgoliaCredentials() && !host) {
        return caller ? ({ ...headers } as AlgoliaSearchOptions) : undefined;
    }

    return {
        hosts: [{ url: host }],
        timeouts: { connect: 30, read: 30, write: 30 },
        ...headers,
    } as AlgoliaSearchOptions;
};

export const createAlgoliaClient = (opts?: SearchClientOptions): SearchClient => {
    if (opts?.admin) {
        const appId = getAlgoliaAppId();
        invariant(appId, '[Algolia] ALGOLIA_APP_ID is not set.');
        invariant(process.env.ALGOLIA_ADMIN_KEY);
        const options = opts.caller ? { headers: { 'x-petm-algolia-caller': opts.caller } } : undefined;
        return algoliasearch(appId, process.env.ALGOLIA_ADMIN_KEY, options);
    }

    return algoliasearch(publicAppId(), publicSearchKey(), buildSearchOptions(getLocale(), opts?.caller));
};
